/**
 * Ready-made room layouts and the placement engine behind the agent's high-level tools:
 * a run of pieces along a wall, a piece beside another, chairs around a table, a whole
 * room from a preset — furniture, rug, curtains, lamps, pictures, plants and the things
 * that stand on tables. Everything here plans; the runtime commits a plan as one undo step.
 *
 * The rules are the ones a designer applies without thinking: backs of beds, sofas and
 * cabinets to a wall with the front turned into the room, nothing across a door or its
 * swing, only low pieces under a window, walkways kept, the longest empty wall for the
 * main piece, a rug under the group, curtains on every window, a lamp on every nightstand.
 */
import type { Item, Level, Room, Vec2, Wall } from '../core/hp/types';
import type { CatalogItem } from './agentCatalog';
import { containsPoint } from '../state/sceneGesture';
import { itemFootprint } from '../core/geometry/footprint';
import { usableCatalogItems, expandQuery } from './catalogFilter';
import { around, beside, frontVector, inFromWall, inwardNormal, openingsOnWall, rightVector, roomBounds, roomWalls, rotationFacing, wallBySide, wallSide, type Placement, type Size, type WallSide } from './layout';
import { FURNISH_PRESETS } from './toolSchemas';

export type Role = 'bed' | 'single_bed' | 'nightstand' | 'table_lamp' | 'wardrobe' | 'dresser' | 'sofa' | 'armchair' | 'coffee_table' | 'tv_stand' | 'tv' | 'dining_table' | 'dining_set' | 'dining_set_large' | 'chair'
  | 'desk' | 'office_chair' | 'monitor' | 'keyboard' | 'fridge' | 'kitchen_set' | 'kitchen_island' | 'sink' | 'hood' | 'kitchen_shelf' | 'kitchen_decor' | 'cutting_board' | 'bath' | 'toilet' | 'basin' | 'mirror' | 'washer' | 'shower' | 'towel_stand'
  | 'floor_lamp' | 'pendant' | 'chandelier' | 'plant' | 'plant_tall' | 'frames' | 'art' | 'books' | 'vase' | 'bench' | 'bookshelf' | 'toys' | 'kids_art' | 'rug' | 'kids_rug' | 'runner' | 'curtains';

interface RolePick { prefer: string[]; query: string; section?: string; maxWidth?: number; minWidth?: number; maxDepth?: number; placement?: CatalogItem['placement']; tags?: string[] }
/** How each role is found in the live catalog: known ids first, then words and tags, then a size sanity check. */
// i18n-ignore-start: search words for the catalog's titles, not interface text
const ROLE_PICKS: Record<Role, RolePick> = {
  bed: { prefer: ['sleeping-lying.bedroom.beds.ikea/1773/1773', 'sleeping-lying.bedroom.beds.gold/022/022'], query: 'кровать', section: 'bed', minWidth: 1.3, tags: ['doublebed'] },
  single_bed: { prefer: ['sleeping-lying.bedroom.beds.ikea/1079/1079'], query: 'кровать', section: 'bed', maxWidth: 1.3 },
  nightstand: { prefer: ['storage.bedroom.nightstand.ikea/289/289'], query: 'тумбочка', maxWidth: .7, maxDepth: .6, tags: ['nightstand', 'bedside'] },
  table_lamp: { prefer: ['lightings.ikea/430/430', 'lightings.gold/4654/4654'], query: 'настольная лампа', maxWidth: .4, tags: ['tablelamp', 'bedsidelamp'] },
  wardrobe: { prefer: ['storage.bedroom.wardrobe.ikea/1728/1728', 'storage.bedroom.wardrobe.ikea/927/927', 'storage.bedroom.wardrobe.ikea/2315/2315'], query: 'шкаф', section: 'storage', tags: ['wardrobe'] },
  dresser: { prefer: ['storage.cabinet.ikea/305/305', 'Imports/6308/6308', 'storage.cabinet.ikea/005/005'], query: 'комод', section: 'storage', tags: ['sideboard', 'dresser', 'buffet'] },
  sofa: { prefer: ['sleeping-lying.livingroom.ikea/2216/2216', 'sleeping-lying.livingroom.ikea/651/651'], query: 'диван', section: 'seating', tags: ['sofa', '3seat'] },
  armchair: { prefer: ['seating.ikea/2348/2348', 'seating.ikea/345/345', 'seating.gold/5154/5154'], query: 'кресло', section: 'seating', tags: ['armchair'] },
  coffee_table: { prefer: ['surfaces.gold/4583/4583', 'surfaces.gold/3459/3459'], query: 'журнальный', section: 'tables', tags: ['coffeetable'] },
  tv_stand: { prefer: ['storage.cabinet.ikea/2274/2274', 'storage.cabinet.ikea/207/207'], query: 'тв', section: 'storage', tags: ['tvstand', 'tvset', 'tvfurniture'] },
  tv: { prefer: ['electronics.gold/1230/1230'], query: 'телевизор', tags: ['tv', '50inch'] },
  dining_table: { prefer: ['surfaces.ikea/2194/2194', 'surfaces.ikea/316/316'], query: 'обеденный', section: 'tables', tags: ['diningtable'] },
  dining_set: { prefer: ['surfaces.ikea/401/401'], query: 'обеденная группа', tags: ['diningset4'] },
  dining_set_large: { prefer: ['surfaces.ikea/409/409'], query: 'обеденная группа', tags: ['diningset6'] },
  chair: { prefer: ['seating.ikea/2220/2220', 'seating.ikea/2154/2154'], query: 'стул', maxWidth: .7, tags: ['chair'] },
  desk: { prefer: ['surfaces.homeoffice/773/773'], query: 'письменный', section: 'tables', tags: ['desk'] },
  office_chair: { prefer: ['seating.homeoffice.gold/572/572', 'seating.ikea/2220/2220'], query: 'офисное кресло', maxWidth: .8, tags: ['officechair', 'chair'] },
  monitor: { prefer: ['electronics.homeoffice.gold/1225/1225', 'electronics.homeoffice.gold/1227/1227'], query: 'монитор', tags: ['monitor'] },
  keyboard: { prefer: ['electronics.homeoffice.gold/1226/1226'], query: 'клавиатура', tags: ['keyboard'] },
  fridge: { prefer: ['electronics.kitchen/1000/1000', 'electronics.kitchen/997/997', 'storage.kitchen.ikea/2066/2066'], query: 'холодильник', section: 'kitchen', tags: ['refrigerators', 'fridge'] },
  kitchen_set: { prefer: ['storage.kitchen.ikea/1074/1074'], query: 'кухня', section: 'kitchen', tags: ['kitchenisland'] },
  kitchen_island: { prefer: ['storage.kitchen/3004/3004'], query: 'кухонный остров', minWidth: 2, tags: ['kitchenisland'] },
  sink: { prefer: ['bathroom.storage/4490/4490', 'bathroom.storage/1952/1952'], query: 'раковина', tags: ['sink'] },
  hood: { prefer: ['storage.cabinet.kitchen.ikea/built-in-appliances/1136/1136'], query: 'вытяжка', section: 'kitchen', tags: ['microwave', 'hood'] },
  kitchen_shelf: { prefer: ['storage.kitchen.ikea/4175/4175', 'storage.kitchen.ikea/1112/1112'], query: 'полка', placement: 'wall', tags: ['openshelve', 'kitchenshelf', 'wallshelve'] },
  kitchen_decor: { prefer: ['decor.kitchen/4623/4623'], query: 'кухонный декор', maxWidth: .5, tags: ['kitchenjars', 'kitchensurface'] },
  cutting_board: { prefer: ['decor.kitchen/4723/4723'], query: 'кухонный декор', maxWidth: .6, tags: ['cuttingboard'] },
  bath: { prefer: ['bathroom.bath/1015/1015', 'bathroom.bath/1054/1054'], query: 'ванна', section: 'bathroom', tags: ['bath'] },
  toilet: { prefer: ['bathroom.toilet/742/742', 'bathroom.toilet/1069/1069'], query: 'унитаз', section: 'bathroom', tags: ['toilet'] },
  basin: { prefer: ['bathroom.storage/1071/1071', 'bathroom.storage/1952/1952', 'bathroom.storage/4490/4490'], query: 'раковина', tags: ['sink'] },
  mirror: { prefer: ['decor.mirrors.gold/4212/4212', 'decor.mirrors.ikea/2461/2461', 'decor.mirrors.gold/2887/2887'], query: 'зеркало', placement: 'wall', tags: ['mirror'] },
  washer: { prefer: ['electronics.bathroom/1061/1061'], query: 'стиральная', tags: ['washer'] },
  shower: { prefer: ['bathroom.shower/759/759', 'bathroom.shower/4998/4998'], query: 'душ', maxWidth: 1.2, tags: ['shower'] },
  towel_stand: { prefer: ['decor.kitchen/3643/3643'], query: 'полотенц', maxWidth: .4, tags: ['towelstand'] },
  floor_lamp: { prefer: ['lightings.ikea/1706/1706', 'lightings.ikea/426/426'], query: 'торшер', section: 'lighting', maxWidth: .5, tags: ['floorlamp'], placement: 'floor' },
  pendant: { prefer: ['lightings.gold/1240/1240', 'lightings.ikea/1860/1860', 'lightings.ikea/420/420'], query: 'подвес', section: 'lighting', placement: 'ceiling', tags: ['pendantlamp', 'ceilinglamp'] },
  chandelier: { prefer: ['lightings.gold/039/039', 'lightings.gold/1244/1244', 'lightings.gold/1240/1240'], query: 'люстра', section: 'lighting', placement: 'ceiling', tags: ['chandelierlamp'] },
  plant: { prefer: ['decor.plants.gold/059/059', 'decor.plants.gold/062/062', 'decor.plants.gold/3795/3795'], query: 'растение', section: 'decor', maxWidth: .7, tags: ['plant', 'homeplant'] },
  plant_tall: { prefer: ['decor.plants.gold/4463/4463', 'decor.plants/224/224', 'decor.plants.gold/227/227'], query: 'растение', section: 'decor', maxWidth: 1, tags: ['floorplant'] },
  frames: { prefer: ['decor.vase/4092/4092'], query: 'рамок', placement: 'wall' },
  art: { prefer: ['decor.paintings.gold/4513/4513', '66859d4d97bca4f78dc51185', 'decor.paintings/082/082-57x77', 'decor.vase/4092/4092'], query: 'картина', placement: 'wall', tags: ['pictures', 'artset', 'painting'] },
  books: { prefer: ['decor.gold/2800/2800', 'decor.gold/688/688', 'decor.gold/5022/5022'], query: 'книги', maxWidth: .45, tags: ['books'] },
  vase: { prefer: ['decor.vase.gold/3383/3383', 'decor.vase/4599/4599', 'decor.vase/4532/4532'], query: 'ваза', maxWidth: .4, tags: ['tablevase', 'vase'] },
  bench: { prefer: ['storage.bedroom.drawer.ikea/958/958', 'seating.bedroom.bench.gold/4690/4690'], query: 'скамья', maxDepth: .6, tags: ['storagebench', 'bench'] },
  bookshelf: { prefer: ['storage.bookcases.ikea/2422/2422'], query: 'стеллаж', tags: ['bookshelve', 'shelving'] },
  toys: { prefer: ['decor.toys/1034/1034', 'decor.toys/1032/1032'], query: 'игрушки', tags: ['toys'] },
  kids_art: { prefer: ['decor.paintings/4094/4094', 'decor.vase/4092/4092'], query: 'картина', placement: 'wall', tags: ['kidsart', 'pictures'] },
  rug: { prefer: [], query: 'ковёр', section: 'rugs', tags: ['rug', 'carpet'] },
  kids_rug: { prefer: ['686ce1aa7260fb4dc7584508'], query: 'ковёр', section: 'rugs', tags: ['kidsrug', 'rug'] },
  runner: { prefer: ['carpets.gold/091/091', '640b92c5389df3004721feea'], query: 'дорожка', section: 'rugs', tags: ['runner'] },
  curtains: { prefer: [], query: 'шторы', section: 'curtains', tags: ['curtains'] },
};
// i18n-ignore-end

/** What each role is called in the notes for the model («no bed in the catalog»). */
export const ROLE_TITLES: Record<Role, string> = {
  bed: 'bed', single_bed: 'bed', nightstand: 'nightstand', table_lamp: 'table lamp', wardrobe: 'wardrobe', dresser: 'dresser', sofa: 'sofa', armchair: 'armchair', coffee_table: 'coffee table',
  tv_stand: 'TV stand', tv: 'TV', dining_table: 'dining table', dining_set: 'dining set', dining_set_large: 'dining set', chair: 'chair', desk: 'desk', office_chair: 'office chair', monitor: 'monitor', keyboard: 'keyboard',
  fridge: 'fridge', kitchen_set: 'kitchen cabinets', kitchen_island: 'kitchen island', sink: 'sink', hood: 'hood', kitchen_shelf: 'shelf', kitchen_decor: 'kitchen decor', cutting_board: 'cutting board',
  bath: 'bathtub', toilet: 'toilet', basin: 'washbasin', mirror: 'mirror', washer: 'washing machine', shower: 'shower', towel_stand: 'towel stand',
  floor_lamp: 'floor lamp', pendant: 'pendant lamp', chandelier: 'chandelier', plant: 'plant', plant_tall: 'plant', frames: 'pictures', art: 'picture', books: 'books', vase: 'vase', bench: 'bench', bookshelf: 'bookcase',
  toys: 'toys', kids_art: 'picture', rug: 'rug', kids_rug: 'rug', runner: 'runner', curtains: 'curtains',
};

const fits = (pick: RolePick, item: CatalogItem) => (!pick.section || item.section === pick.section)
  && (!pick.placement || item.placement === pick.placement)
  && (pick.maxWidth === undefined || item.size[0] <= pick.maxWidth) && (pick.minWidth === undefined || item.size[0] >= pick.minWidth)
  && (pick.maxDepth === undefined || item.size[2] <= pick.maxDepth);
const readyFirst = (list: CatalogItem[]) => [...list].sort((a, b) => Number(b.converted) - Number(a.converted));

/** Every catalog item that can play a role, best first: known ids in order, then tag matches, then title matches. */
export function candidatesForRole(role: Role, items: readonly CatalogItem[]): CatalogItem[] {
  const pick = ROLE_PICKS[role];
  const usable = usableCatalogItems(items);
  const out: CatalogItem[] = [];
  for (const id of pick.prefer) { const exact = usable.find(item => item.id === id); if (exact && !out.includes(exact)) out.push(exact); }
  const terms = expandQuery(pick.query);
  const byTag = pick.tags ? usable.filter(item => item.tags.some(tag => pick.tags!.includes(tag.toLowerCase())) && fits(pick, item)) : [];
  const byWord = usable.filter(item => terms.some(term => item.title.toLowerCase().includes(term)) && fits(pick, item));
  for (const item of [...readyFirst(byTag), ...readyFirst(byWord)]) if (!out.includes(item)) out.push(item);
  return out;
}
/** The catalog item for a role: a known id when the catalog still has it, otherwise the best-fitting tag or word match, otherwise nothing. */
export function pickForRole(role: Role, items: readonly CatalogItem[]): CatalogItem | undefined { return candidatesForRole(role, items)[0]; }

export const sizeOf = (entry: CatalogItem): Size => ({ w: entry.size[0], h: entry.size[1], d: entry.size[2] });

export interface Planned { entry: CatalogItem; role?: Role; position: Vec2; rotation: number; elevation: number; wallId?: string; note?: string }
export interface PlanResult { placed: Planned[]; skipped: string[] }

/** The stretches of a wall (metres from its start) a piece of this height may not stand in front of. */
export function blockedSpans(level: Level, wall: Wall, height: number): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  for (const opening of openingsOnWall(level, wall)) {
    const isWindow = opening.kind === 'window';
    if (isWindow && height <= opening.sill + .02) continue;
    const clearance = isWindow ? .05 : .3; // a door needs room to swing and to be walked through
    spans.push([opening.offset - opening.width / 2 - clearance, opening.offset + opening.width / 2 + clearance]);
  }
  return spans.sort((a, b) => a[0] - b[0]);
}

/** Free stretches of a wall for a piece of this height, longest first. */
export function freeSpans(level: Level, wall: Wall, height: number): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  let cursor = 0;
  for (const [from, to] of blockedSpans(level, wall, height)) { if (from > cursor) spans.push([cursor, from]); cursor = Math.max(cursor, to); }
  if (cursor < wall.length) spans.push([cursor, wall.length]);
  return spans.filter(([a, b]) => b - a > .1).sort((a, b) => (b[1] - b[0]) - (a[1] - a[0]));
}

const footprintOf = (position: Vec2, rotation: number, size: Size): [Vec2, Vec2, Vec2, Vec2] =>
  itemFootprint({ id: '', modelRef: { kind: 'none', id: '', raw: '' }, placement: 'floor', position, elevation: 0, rotation, size, scale: [1, 1, 1], mirrored: false, pivot: 'bottom' });

function overlapDepth(a: readonly Vec2[], b: readonly Vec2[]): number {
  let depth = Infinity;
  for (const polygon of [a, b]) for (let i = 0; i < polygon.length; i++) {
    const p = polygon[i], q = polygon[(i + 1) % polygon.length];
    const length = Math.hypot(q.x - p.x, q.z - p.z) || 1, axis = { x: -(q.z - p.z) / length, z: (q.x - p.x) / length };
    const project = (points: readonly Vec2[]) => points.reduce((range, point) => { const value = point.x * axis.x + point.z * axis.z; return [Math.min(range[0], value), Math.max(range[1], value)]; }, [Infinity, -Infinity]);
    const [aMin, aMax] = project(a), [bMin, bMax] = project(b);
    const overlap = Math.min(aMax, bMax) - Math.max(aMin, bMin);
    if (overlap <= 0) return 0;
    depth = Math.min(depth, overlap);
  }
  return depth;
}

/** Items the footprint would run into on the floor by more than a few centimetres (rugs, ceiling items, what hangs or stands above 30 cm never block). */
export function collisions(level: Level, position: Vec2, rotation: number, size: Size, planned: readonly Planned[] = [], ignore?: string): string[] {
  const footprint = footprintOf(position, rotation, size);
  const hits: string[] = [];
  const bodies: Array<{ id: string; caption?: string; position: Vec2; rotation: number; size: Size; placement: Item['placement']; elevation: number }> = [
    ...level.items, ...planned.map((p, i) => ({ id: `planned-${i}`, caption: p.entry.title, position: p.position, rotation: p.rotation, size: sizeOf(p.entry), placement: p.entry.placement, elevation: p.elevation })),
  ];
  for (const other of bodies) {
    if (other.id === ignore || other.placement === 'ceiling' || other.elevation > .3 || other.size.h < .08) continue;
    if (overlapDepth(footprint, footprintOf(other.position, other.rotation, other.size)) > .03) hits.push(other.id.startsWith('planned-') ? other.caption ?? 'planned item' : other.id);
  }
  return hits;
}

export function insideRoom(room: Room, position: Vec2, rotation: number, size: Size): boolean {
  return footprintOf(position, rotation, size).every(corner => containsPoint(room.polygon, corner));
}

export interface RunOptions { from?: 'start' | 'end' | 'center'; offset?: number; gap?: number; elevation?: number; planned?: readonly Planned[]; /** Only a layout that runs into nothing will do (presets); otherwise the asked layout is returned with overlap notes. */ strict?: boolean; /** Floor pieces turned 90°, their long side along the wall (a single bed lengthwise). */ sideways?: boolean }

/**
 * A run of pieces along a wall in a free stretch: `from` picks which end of the stretch,
 * `center` centres the run in the longest stretch. Wall items in the list hang on the wall
 * above the same spot (a wall cabinet over its base). Fails with the reason when nothing fits.
 */
export function planRun(level: Level, room: Room, wall: Wall, entries: readonly CatalogItem[], options: RunOptions = {}): { placed: Planned[]; error?: string } {
  if (!entries.length) return { placed: [] };
  const gap = options.gap ?? .02, offset = options.offset ?? .05;
  const turned = (entry: CatalogItem): Size => options.sideways && entry.placement === 'floor' ? { w: entry.size[2], d: entry.size[0], h: entry.size[1] } : sizeOf(entry);
  const tallest = Math.max(...entries.map(entry => entry.size[1] + (entry.placement === 'wall' ? 1.2 : 0)));
  const total = entries.reduce((sum, entry) => sum + turned(entry).w, 0) + gap * (entries.length - 1);
  const spans = freeSpans(level, wall, tallest).filter(([a, b]) => b - a >= total + offset);
  if (!spans.length) {
    const all = freeSpans(level, wall, tallest);
    return { placed: [], error: `${fmtLen(total)} m of ${entries.map(e => e.title).join(', ')} does not fit on ${wallSideLabel(room, wall)} wall (${fmtLen(wall.length)} m${all.length ? `, free stretches ${all.map(([a, b]) => fmtLen(b - a) + ' m').join(', ')}` : ', all in front of openings'}).` };
  }
  const n = inwardNormal(room, wall);
  const rotation = rotationFacing(n);
  const layout = (span: [number, number], from: RunOptions['from']): Planned[] => {
    const start = from === 'end' ? span[1] - offset : from === 'center' ? span[0] + (span[1] - span[0] - total) / 2 : span[0] + offset;
    const direction = from === 'end' ? -1 : 1;
    let cursor = start;
    const placed: Planned[] = [];
    for (const entry of entries) {
      const size = turned(entry);
      const wallItem = entry.placement === 'wall';
      const turn = options.sideways && entry.placement === 'floor' ? rotation + Math.PI / 2 : rotation;
      const along = cursor + direction * size.w / 2;
      const reach = wall.thickness / 2 + size.d / 2 + .005;
      const position = { x: wall.start.x + wall.dir.x * along + n.x * reach, z: wall.start.z + wall.dir.z * along + n.z * reach };
      const elevation = wallItem ? Math.max(0, Math.min(wall.height - size.h, options.elevation ?? 1.45)) : entry.placement === 'ceiling' ? Math.max(0, level.height - size.h) : Math.max(0, options.elevation ?? 0);
      const hits = wallItem || elevation > .3 ? [] : collisions(level, position, turn, sizeOf(entry), [...(options.planned ?? []), ...placed]);
      placed.push({ entry, position: round(position), rotation: turn, elevation, ...(wallItem ? { wallId: wall.id } : {}), ...(hits.length ? { note: `overlaps ${hits.join(', ')}` } : {}) });
      cursor += direction * (size.w + gap);
    }
    return placed;
  };
  const asked = layout(spans[0], options.from);
  if (!options.strict || asked.every(p => !p.note)) return { placed: asked };
  const froms: RunOptions['from'][] = [options.from ?? 'start', 'start', 'end', 'center'];
  for (const span of spans) for (const from of froms) {
    const candidate = layout(span, from);
    if (candidate.every(p => !p.note)) return { placed: candidate };
  }
  return { placed: [], error: `${entries.map(e => e.title).join(', ')}: every free stretch of ${wallSideLabel(room, wall)} wall runs into something.` };
}

/** The wall for the main piece: the asked side, else the preferred wall, else the longest free stretch, doors avoided, windows allowed only for low pieces. */
export function pickWall(level: Level, room: Room, height: number, side?: WallSide, avoid: readonly Wall[] = [], need = 0, prefer?: Wall): Wall | undefined {
  return candidateWalls(level, room, height, side, avoid, need, prefer)[0];
}
/** Walls that could take a piece of this height and width, best first: the asked side alone, else the preferred wall, then the longest free stretch. */
export function candidateWalls(level: Level, room: Room, height: number, side?: WallSide, avoid: readonly Wall[] = [], need = 0, prefer?: Wall): Wall[] {
  if (side) { const wall = wallBySide(level, room, side); return wall ? [wall] : []; }
  const candidates = roomWalls(level, room).filter(wall => !avoid.includes(wall));
  const scored = candidates.map(wall => ({ wall, free: freeSpans(level, wall, height)[0] })).filter(({ free }) => free && free[1] - free[0] >= need)
    .sort((a, b) => Number(b.wall === prefer) - Number(a.wall === prefer) || (b.free![1] - b.free![0]) - (a.free![1] - a.free![0]) || b.wall.length - a.wall.length);
  return scored.map(({ wall }) => wall);
}

const oppositeSide: Record<WallSide, WallSide> = { north: 'south', south: 'north', east: 'west', west: 'east' };
export function oppositeWall(level: Level, room: Room, wall: Wall): Wall | undefined { return wallBySide(level, room, oppositeSide[wallSide(room, wall)]); }

/** A piece next to another, on the requested side, unless it would leave the room or run into something. */
export function planBeside(level: Level, room: Room | undefined, anchor: Pick<Item, 'id' | 'position' | 'rotation' | 'size'>, entry: CatalogItem, side: 'left' | 'right' | 'front' | 'behind', gap = .05, planned: readonly Planned[] = []): { placed?: Planned; error?: string } {
  const size = sizeOf(entry);
  const placement = beside(anchor, size, side, gap);
  if (room && !insideRoom(room, placement.position, placement.rotation, size)) return { error: `${entry.title} ${side} of ${anchor.id} would stand outside room #${room.index}.` };
  const hits = collisions(level, placement.position, placement.rotation, size, planned, anchor.id);
  return { placed: { entry, position: placement.position, rotation: placement.rotation, elevation: entry.placement === 'ceiling' ? Math.max(0, level.height - size.h) : 0, ...(hits.length ? { note: `overlaps ${hits.join(', ')}` } : {}) } };
}

/** Chairs around a table, dropped where they would leave the room. */
export function planAround(level: Level, room: Room | undefined, table: Pick<Item, 'id' | 'position' | 'rotation' | 'size'>, entry: CatalogItem, count: number, planned: readonly Planned[] = []): Planned[] {
  const size = sizeOf(entry);
  const placements = around(table, size, count);
  const result: Planned[] = [];
  for (const placement of placements) {
    if (room && !insideRoom(room, placement.position, placement.rotation, size)) continue;
    const hits = collisions(level, placement.position, placement.rotation, size, [...planned, ...result], table.id);
    result.push({ entry, position: placement.position, rotation: placement.rotation, elevation: 0, ...(hits.length ? { note: `overlaps ${hits.join(', ')}` } : {}) });
  }
  return result;
}

export type Preset = typeof FURNISH_PRESETS[number];
export const PRESETS: readonly Preset[] = FURNISH_PRESETS;
export const PRESET_FOR_ROOM_TYPE: Record<string, Preset> = { bedroom: 'bedroom', kids: 'kids', livingroom: 'living', diningroom: 'dining', kitchen: 'kitchen', bathroom: 'bathroom', office: 'office' };

const fmtLen = (value: number) => (Math.round(value * 100) / 100).toString();
const wallSideLabel = (room: Room, wall: Wall) => `the ${wallSide(room, wall)}`;
const round = (p: Vec2): Vec2 => ({ x: Math.round(p.x * 1e4) / 1e4, z: Math.round(p.z * 1e4) / 1e4 });
const add = (a: Vec2, b: Vec2, k = 1): Vec2 => ({ x: a.x + b.x * k, z: a.z + b.z * k });

interface Ctx { level: Level; room: Room; catalog: readonly CatalogItem[]; placed: Planned[]; skipped: string[]; used: Wall[] }

function want(ctx: Ctx, role: Role): CatalogItem | undefined {
  const entry = pickForRole(role, ctx.catalog);
  if (!entry) ctx.skipped.push(`no ${ROLE_TITLES[role]} in the catalog`);
  return entry;
}
interface RunWhere { side?: WallSide; avoid?: readonly Wall[]; prefer?: Wall; from?: RunOptions['from']; offset?: number; sideways?: boolean; elevation?: number }
/** A run on the first candidate wall where it stands clear of everything; the asked side is the only candidate when given. */
function commitRun(ctx: Ctx, entries: CatalogItem[], roles: Role[], where: RunWhere = {}): Planned[] {
  if (!entries.length) return [];
  const tallest = Math.max(...entries.map(entry => entry.size[1]));
  const need = entries.reduce((sum, entry) => sum + (where.sideways && entry.placement === 'floor' ? entry.size[2] : entry.size[0]), 0) + (where.offset ?? .05) + .02 * (entries.length - 1);
  const walls = candidateWalls(ctx.level, ctx.room, tallest, where.side, where.avoid, need, where.prefer);
  let reason = `${entries.map(e => e.title).join(', ')}: no free wall`;
  for (const wall of walls) {
    const result = planRun(ctx.level, ctx.room, wall, entries, { from: where.from, offset: where.offset, planned: ctx.placed, strict: true, sideways: where.sideways, elevation: where.elevation });
    if (result.error) { reason = result.error; continue; }
    result.placed.forEach((p, i) => { p.role = roles[i]; });
    ctx.placed.push(...result.placed);
    if (!ctx.used.includes(wall)) ctx.used.push(wall);
    return result.placed;
  }
  ctx.skipped.push(reason);
  return [];
}
const anchorOf = (p: Planned) => ({ id: p.entry.title, position: p.position, rotation: p.rotation, size: sizeOf(p.entry) });
/** The wall a placed piece stands against, if any. */
function wallBehind(ctx: Ctx, p: Planned): Wall | undefined {
  const size = sizeOf(p.entry);
  return roomWalls(ctx.level, ctx.room).find(wall => {
    const along = (p.position.x - wall.start.x) * wall.dir.x + (p.position.z - wall.start.z) * wall.dir.z;
    const away = Math.abs((p.position.x - wall.start.x) * wall.normalIn.x + (p.position.z - wall.start.z) * wall.normalIn.z);
    return along > -.01 && along < wall.length + .01 && away < Math.max(size.d, size.w) / 2 + wall.thickness / 2 + .05;
  });
}
/** A piece beside another, trying the sides in order until one is inside the room and clear. */
function commitBeside(ctx: Ctx, anchor: Planned | undefined, role: Role, sides: readonly ('left' | 'right' | 'front' | 'behind')[], gap = .05, quiet = false): Planned | undefined {
  if (!anchor) return undefined;
  const entry = quiet ? pickForRole(role, ctx.catalog) : want(ctx, role);
  if (!entry) return undefined;
  let reason = '';
  for (const side of sides) {
    const result = planBeside(ctx.level, ctx.room, anchorOf(anchor), entry, side, gap, ctx.placed);
    if (result.placed && !result.placed.note) { result.placed.role = role; ctx.placed.push(result.placed); return result.placed; }
    reason = result.error ?? `${entry.title}: ${result.placed?.note} ${side} of ${anchor.entry.title}`;
  }
  if (!quiet) ctx.skipped.push(reason);
  return undefined;
}
/** Something that stands on a piece: a lamp on a nightstand, a TV on its stand, books on the coffee table. */
function commitOnTop(ctx: Ctx, base: Planned | undefined, role: Role, along = 0, across = 0): Planned | undefined {
  if (!base) return undefined;
  const entry = pickForRole(role, ctx.catalog);
  if (!entry || entry.placement !== 'floor') return undefined;
  const baseSize = sizeOf(base.entry), size = sizeOf(entry);
  if (size.w > baseSize.w + .05 || size.d > baseSize.d + .3) return undefined;
  const position = round(add(add(base.position, rightVector(base.rotation), along), frontVector(base.rotation), across));
  const planned: Planned = { entry, role, position, rotation: base.rotation, elevation: Math.round((base.elevation + baseSize.h) * 1e3) / 1e3 };
  ctx.placed.push(planned);
  return planned;
}
function commitCeiling(ctx: Ctx, role: Role, at: Vec2): void {
  const entry = pickForRole(role, ctx.catalog) ?? (role === 'chandelier' ? want(ctx, 'pendant') : want(ctx, role));
  if (!entry) return;
  ctx.placed.push({ entry, role, position: round(at), rotation: 0, elevation: Math.max(0, ctx.level.height - entry.size[1]) });
}
/** Wall decor centred over a piece, at eye height: `picked`, else the widest picture narrower than the piece, or the given role's pick. */
function commitWallDecor(ctx: Ctx, over: Planned | undefined, role: Role, elevation = 1.5, picked?: CatalogItem): Planned | undefined {
  if (!over) return undefined;
  const wall = wallBehind(ctx, over);
  if (!wall) return undefined;
  const overWidth = sizeOf(over.entry).w;
  const entry = picked ?? (role === 'art' || role === 'kids_art'
    ? candidatesForRole(role, ctx.catalog).filter(item => item.placement === 'wall' && item.size[0] <= overWidth - .1).sort((a, b) => b.size[0] - a.size[0])[0] ?? pickForRole('frames', ctx.catalog)
    : pickForRole(role, ctx.catalog));
  if (!entry || entry.placement !== 'wall') return undefined;
  const along = (over.position.x - wall.start.x) * wall.dir.x + (over.position.z - wall.start.z) * wall.dir.z;
  const size = sizeOf(entry);
  const n = inwardNormal(ctx.room, wall);
  const reach = wall.thickness / 2 + size.d / 2 + .005;
  const clamped = Math.max(size.w / 2, Math.min(wall.length - size.w / 2, along));
  const planned: Planned = { entry, role, wallId: wall.id, position: round({ x: wall.start.x + wall.dir.x * clamped + n.x * reach, z: wall.start.z + wall.dir.z * clamped + n.z * reach }), rotation: rotationFacing(n), elevation: Math.max(0, Math.min(wall.height - size.h, elevation - size.h / 2)) };
  ctx.placed.push(planned);
  return planned;
}
/** A corner of the room that is still empty, for a plant or a floor lamp. */
function freeCorner(ctx: Ctx, size: Size): Vec2 | undefined {
  const { minX, maxX, minZ, maxZ } = roomBounds(ctx.room);
  const inset = Math.max(size.w, size.d) / 2 + .12;
  const corners = [{ x: maxX - inset, z: minZ + inset }, { x: maxX - inset, z: maxZ - inset }, { x: minX + inset, z: minZ + inset }, { x: minX + inset, z: maxZ - inset }];
  return corners.find(corner => containsPoint(ctx.room.polygon, corner) && insideRoom(ctx.room, corner, 0, size) && !collisions(ctx.level, corner, 0, size, ctx.placed).length
    && !ctx.level.openings.some(opening => { const wall = ctx.level.walls.find(w => w.id === opening.wallId); if (!wall) return false; const at = { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset }; return Math.hypot(at.x - corner.x, at.z - corner.z) < opening.width / 2 + .6; }));
}
function commitCorner(ctx: Ctx, role: Role): Planned | undefined {
  const entry = pickForRole(role, ctx.catalog);
  if (!entry) return undefined;
  const corner = freeCorner(ctx, sizeOf(entry));
  if (!corner) return undefined;
  const planned: Planned = { entry, role, position: corner, rotation: 0, elevation: 0 };
  ctx.placed.push(planned);
  return planned;
}
/** The rug under a group of pieces: the largest that covers the group with a margin and still lies inside the room, turned to fit. */
function commitRug(ctx: Ctx, group: readonly Planned[], role: Role = 'rug'): Planned | undefined {
  const floor = group.filter(p => p.entry.placement === 'floor' && p.elevation < .1);
  if (!floor.length) return undefined;
  const corners = floor.flatMap(p => footprintOf(p.position, p.rotation, sizeOf(p.entry)));
  const minX = Math.min(...corners.map(c => c.x)), maxX = Math.max(...corners.map(c => c.x)), minZ = Math.min(...corners.map(c => c.z)), maxZ = Math.max(...corners.map(c => c.z));
  const centre = { x: (minX + maxX) / 2, z: (minZ + maxZ) / 2 };
  const wantW = maxX - minX + .5, wantD = maxZ - minZ + .5;
  const bounds = roomBounds(ctx.room);
  // A special rug role (the kids rug) takes only its own kind; the plain role takes any rug that fits.
  const kind = role === 'rug' ? undefined : ROLE_PICKS[role].tags?.[0];
  const rugs = candidatesForRole(role, ctx.catalog).filter(item => item.size[1] <= .08 && (!kind || item.tags.some(tag => tag.toLowerCase() === kind)));
  // A rug slides into the room when the group stands against a wall: clamped to the outline, then checked.
  const options = rugs.flatMap(item => [{ item, rotation: 0, w: item.size[0], d: item.size[2] }, { item, rotation: Math.PI / 2, w: item.size[2], d: item.size[0] }])
    .map(option => ({ ...option, at: { x: Math.max(bounds.minX + option.w / 2 + .1, Math.min(bounds.maxX - option.w / 2 - .1, centre.x)), z: Math.max(bounds.minZ + option.d / 2 + .1, Math.min(bounds.maxZ - option.d / 2 - .1, centre.z)) } }))
    .filter(option => insideRoom(ctx.room, option.at, option.rotation, { w: option.w, d: option.d, h: 0 }))
    .sort((a, b) => Math.abs(a.w - wantW) + Math.abs(a.d - wantD) - (Math.abs(b.w - wantW) + Math.abs(b.d - wantD)));
  const best = options.find(option => option.w >= wantW - .8 && option.d >= wantD - .8) ?? options[0];
  if (!best) return undefined;
  const planned: Planned = { entry: best.item, role, position: round(best.at), rotation: best.rotation, elevation: 0 };
  ctx.placed.push(planned);
  return planned;
}
/** Curtains on every window of the room: wider than the window, no taller than the room, against the wall over the sill, hung on it when a wall piece. */
function commitCurtains(ctx: Ctx): void {
  const all = candidatesForRole('curtains', ctx.catalog).filter(item => item.placement !== 'ceiling' && item.size[1] > 1.2);
  if (!all.length) return;
  for (const wall of roomWalls(ctx.level, ctx.room)) for (const opening of openingsOnWall(ctx.level, wall)) {
    if (opening.kind !== 'window') continue;
    const plain = (item: CatalogItem) => Number(item.tags.some(tag => /^(Roman|Blinds)$/i.test(tag)));
    const fitting = all.filter(item => item.size[0] >= opening.width + .3 && item.size[1] <= ctx.level.height + .1).sort((a, b) => plain(a) - plain(b) || a.size[0] - b.size[0]);
    const entry = fitting[0] ?? [...all].sort((a, b) => Math.abs(a.size[0] - opening.width - .4) - Math.abs(b.size[0] - opening.width - .4))[0];
    const size = sizeOf(entry);
    const n = inwardNormal(ctx.room, wall);
    const along = Math.max(size.w / 2, Math.min(wall.length - size.w / 2, opening.offset));
    const reach = wall.thickness / 2 + size.d / 2 + .005;
    ctx.placed.push({ entry, role: 'curtains', position: round({ x: wall.start.x + wall.dir.x * along + n.x * reach, z: wall.start.z + wall.dir.z * along + n.z * reach }), rotation: rotationFacing(n), elevation: 0, ...(entry.placement === 'wall' ? { wallId: wall.id } : {}) });
  }
}
/** A table across from a wall run, its long side parallel to the wall, chairs around it; a dining group is one piece with its chairs built in. */
function commitTable(ctx: Ctx, facing: Wall | undefined, tableRole: Role, chairs: number, distance: number): Planned | undefined {
  const table = want(ctx, tableRole);
  if (!table) return undefined;
  const size = sizeOf(table);
  const wall = facing ?? pickWall(ctx.level, ctx.room, size.h);
  const chair = chairs > 0 ? pickForRole('chair', ctx.catalog) : undefined;
  const chairDepth = chair ? chair.size[2] : 0;
  let position: Vec2, rotation: number;
  if (wall) {
    const n = inwardNormal(ctx.room, wall);
    rotation = rotationFacing(n);
    position = inFromWall(ctx.room, wall, distance + chairDepth + size.d / 2 + .05);
  } else { position = roomBounds(ctx.room).centre; rotation = 0; }
  const candidates = [position, roomBounds(ctx.room).centre];
  const spot = candidates.find(p => insideRoom(ctx.room, p, rotation, size) && !collisions(ctx.level, p, rotation, size, ctx.placed).length);
  if (!spot) { ctx.skipped.push(`${table.title}: no clear spot for it`); return undefined; }
  const planned: Planned = { entry: table, role: tableRole, position: round(spot), rotation, elevation: 0 };
  ctx.placed.push(planned);
  if (chair && chairs > 0) {
    const seats = planAround(ctx.level, ctx.room, anchorOf(planned), chair, chairs, ctx.placed).filter(seat => !seat.note);
    seats.forEach(seat => { seat.role = 'chair'; });
    ctx.placed.push(...seats);
    if (seats.length < chairs) ctx.skipped.push(`${chairs - seats.length} chair(s): no room around the table`);
  } else if (!chair && chairs > 0) ctx.skipped.push('no chair in the catalog');
  return planned;
}
/** A dining group when the catalog has one and it fits across from the run, else against a free wall, else a table with chairs. */
function commitDining(ctx: Ctx, facing: Wall | undefined, large: boolean, chairs: number, walkway = .9): Planned | undefined {
  const role: Role = large ? 'dining_set_large' : 'dining_set';
  const set = pickForRole(role, ctx.catalog);
  const before = ctx.skipped.length;
  if (set) {
    const placed = commitTable(ctx, facing, role, 0, walkway);
    if (placed) return placed;
    ctx.skipped.length = before;
    const [byWall] = commitRun(ctx, [set], [role], { avoid: ctx.used, from: 'center' });
    if (byWall) return byWall;
    ctx.skipped.length = before;
  }
  const table = commitTable(ctx, facing, 'dining_table', chairs, walkway);
  if (table) return table;
  ctx.skipped.length = before;
  const small = pickForRole('dining_table', ctx.catalog);
  const [byWall] = small ? commitRun(ctx, [small], ['dining_table'], { avoid: ctx.used, from: 'center' }) : [];
  if (byWall) { const chair = pickForRole('chair', ctx.catalog); if (chair) { const seats = planAround(ctx.level, ctx.room, anchorOf(byWall), chair, Math.min(chairs, 3), ctx.placed).filter(seat => !seat.note); seats.forEach(seat => { seat.role = 'chair'; }); ctx.placed.push(...seats); } return byWall; }
  ctx.skipped.push(`${(set ?? small)?.title ?? 'dining table'}: the room is too small for it`);
  return undefined;
}
/** A desk with its back to a wall — under the window when there is one — and a chair in front of it. */
function commitDesk(ctx: Ctx, side: WallSide | undefined, office: boolean): Planned | undefined {
  const desk = want(ctx, 'desk');
  if (!desk) return undefined;
  const windowWall = roomWalls(ctx.level, ctx.room).find(wall => !ctx.used.includes(wall) && openingsOnWall(ctx.level, wall).some(opening => opening.kind === 'window'));
  const [placedDesk] = commitRun(ctx, [desk], ['desk'], { side, avoid: ctx.used, prefer: windowWall, from: 'center' });
  if (!placedDesk) return undefined;
  commitBeside(ctx, placedDesk, office ? 'office_chair' : 'chair', ['front'], .1);
  if (office) { commitOnTop(ctx, placedDesk, 'monitor', 0, -.1); commitOnTop(ctx, placedDesk, 'keyboard', 0, .15); }
  return placedDesk;
}
/** The wall opposite the room's door, when it has one — beds and sofas like to face the door. */
function acrossFromDoor(ctx: Ctx): Wall | undefined {
  const doorWall = roomWalls(ctx.level, ctx.room).find(wall => openingsOnWall(ctx.level, wall).some(opening => opening.kind !== 'window'));
  return doorWall ? oppositeWall(ctx.level, ctx.room, doorWall) : undefined;
}

/** A whole room from a preset; `side` puts the main piece (bed, sofa, kitchen run, desk) against that wall. */
export function planPreset(level: Level, room: Room, preset: Preset, catalog: readonly CatalogItem[], side?: WallSide): PlanResult {
  const ctx: Ctx = { level, room, catalog, placed: [], skipped: [], used: [] };
  const { centre, minX, maxX, minZ, maxZ } = roomBounds(room);
  const small = Math.min(maxX - minX, maxZ - minZ) < 2.2;
  const roomy = (maxX - minX) * (maxZ - minZ) >= 12;
  switch (preset) {
    case 'bedroom': {
      const bed = want(ctx, 'bed');
      const [placedBed] = bed ? commitRun(ctx, [bed], ['bed'], { side, prefer: acrossFromDoor(ctx), from: 'center' }) : [];
      if (placedBed) {
        const left = commitBeside(ctx, placedBed, 'nightstand', ['left'], .05, true), right = commitBeside(ctx, placedBed, 'nightstand', ['right'], .05, true);
        if (!left && !right) ctx.skipped.push(pickForRole('nightstand', catalog) ? 'nightstands: no room beside the bed' : 'no nightstand in the catalog');
        commitOnTop(ctx, left, 'table_lamp'); commitOnTop(ctx, right, 'table_lamp');
        if (!left && !right && !small) commitBeside(ctx, placedBed, 'floor_lamp', ['left', 'right'], .1, true);
        commitWallDecor(ctx, placedBed, 'art', 1.6);
        const bench = small ? undefined : commitBeside(ctx, placedBed, 'bench', ['front'], .15, true);
        commitRug(ctx, [placedBed, ...[left, right, bench].filter((p): p is Planned => !!p)]);
      }
      const wardrobe = want(ctx, 'wardrobe');
      if (wardrobe) commitRun(ctx, [wardrobe], ['wardrobe'], { avoid: ctx.used, from: 'start', offset: .1 });
      if (roomy) { const dresser = pickForRole('dresser', catalog); if (dresser) { const before = ctx.skipped.length; commitRun(ctx, [dresser], ['dresser'], { avoid: ctx.used, from: 'center' }); ctx.skipped.length = before; } }
      commitCurtains(ctx);
      if (!small) commitCorner(ctx, 'plant_tall');
      commitCeiling(ctx, 'pendant', centre);
      break;
    }
    case 'kids': {
      const bed = want(ctx, 'single_bed');
      // A single bed lies lengthwise along the wall from a corner.
      const [placedBed] = bed ? commitRun(ctx, [bed], ['bed'], { side, from: 'start', offset: .05, sideways: true }) : [];
      const desk = commitDesk(ctx, undefined, false);
      commitOnTop(ctx, desk, 'toys', .25, 0);
      commitWallDecor(ctx, desk ?? placedBed, 'kids_art', 1.45);
      const wardrobe = want(ctx, 'wardrobe');
      if (wardrobe) commitRun(ctx, [wardrobe], ['wardrobe'], { avoid: ctx.used, from: 'start', offset: .1 });
      if (placedBed) commitRug(ctx, [placedBed], 'kids_rug') ?? commitRug(ctx, [placedBed]);
      commitCorner(ctx, 'toys');
      commitCurtains(ctx);
      commitCeiling(ctx, 'pendant', centre);
      break;
    }
    case 'living': {
      const sofa = want(ctx, 'sofa');
      const [placedSofa] = sofa ? commitRun(ctx, [sofa], ['sofa'], { side, prefer: acrossFromDoor(ctx), from: 'center' }) : [];
      const group: Planned[] = [];
      if (placedSofa) {
        group.push(placedSofa);
        const coffee = commitBeside(ctx, placedSofa, 'coffee_table', ['front'], .45);
        if (coffee) { group.push(coffee); commitOnTop(ctx, coffee, 'books', -.3, 0); commitOnTop(ctx, coffee, 'vase', .3, 0); }
        if (!small) { const chair = commitBeside(ctx, placedSofa, 'armchair', ['left', 'right'], .3, true) ?? (coffee ? commitBeside(ctx, coffee, 'armchair', ['left', 'right'], .2) : undefined); if (chair) group.push(chair); }
        if (!small) commitBeside(ctx, placedSofa, 'floor_lamp', ['right', 'left'], .15, true);
        commitWallDecor(ctx, placedSofa, 'art', 1.55);
        const tv = want(ctx, 'tv_stand');
        const sofaWall = ctx.used[0];
        const tvWall = sofaWall && oppositeWall(level, room, sofaWall);
        const [stand] = tv && tvWall ? commitRun(ctx, [tv], ['tv_stand'], { side: wallSide(room, tvWall), from: 'center' }) : [];
        commitOnTop(ctx, stand, 'tv');
        commitRug(ctx, group);
      }
      if (roomy) { const shelf = pickForRole('bookshelf', catalog); if (shelf) { const before = ctx.skipped.length; commitRun(ctx, [shelf], ['bookshelf'], { avoid: ctx.used, from: 'end', offset: .1 }); ctx.skipped.length = before; } }
      commitCurtains(ctx);
      if (!small) { commitCorner(ctx, 'plant_tall'); commitCorner(ctx, 'plant'); }
      commitCeiling(ctx, roomy ? 'chandelier' : 'pendant', centre);
      break;
    }
    case 'dining': {
      const table = commitDining(ctx, undefined, roomy, 6);
      if (table) { commitRug(ctx, [table]); commitCeiling(ctx, 'pendant', table.position); } else commitCeiling(ctx, 'pendant', centre);
      const dresser = want(ctx, 'dresser');
      const [sideboard] = dresser ? commitRun(ctx, [dresser], ['dresser'], { from: 'center' }) : [];
      commitWallDecor(ctx, sideboard, 'art', 1.5);
      commitOnTop(ctx, sideboard, 'vase', .3, 0);
      commitCurtains(ctx);
      if (!small) commitCorner(ctx, 'plant');
      break;
    }
    case 'kitchen': {
      const fridge = pickForRole('fridge', catalog), set = pickForRole('kitchen_set', catalog), island = pickForRole('kitchen_island', catalog), sink = pickForRole('sink', catalog);
      if (!set && !island) ctx.skipped.push('no kitchen set in the catalog');
      const longest = Math.max(0, ...roomWalls(level, room).map(wall => (freeSpans(level, wall, 2)[0] ?? [0, 0])).map(([a, b]) => b - a));
      const counter = island && longest >= (fridge?.size[0] ?? 0) + island.size[0] + (sink?.size[0] ?? 0) + .2 ? island : set;
      const run = [fridge, counter, sink].filter((entry): entry is CatalogItem => !!entry);
      const roles = [fridge && 'fridge', counter && (counter === island ? 'kitchen_island' : 'kitchen_set'), sink && 'sink'].filter((role): role is Role => !!role);
      let placed = run.length ? commitRun(ctx, run, roles, { side, from: 'start' }) : [];
      if (!placed.length && run.length > 1) {
        // One wall is too short for the whole run: the counter and the sink together, the fridge on another wall.
        ctx.skipped.pop();
        const shortRun = [set ?? counter, sink].filter((entry): entry is CatalogItem => !!entry);
        placed = commitRun(ctx, shortRun, ['kitchen_set', 'sink'], { side, from: 'start' });
        if (fridge) commitRun(ctx, [fridge], ['fridge'], { avoid: ctx.used, from: 'end', offset: .1 });
      }
      const cooking = ctx.placed.find(p => p.role === 'kitchen_set' || p.role === 'kitchen_island');
      const hood = pickForRole('hood', catalog);
      if (cooking && hood?.placement === 'wall') commitWallDecor(ctx, cooking, 'hood', 1.5 + hood.size[1] / 2, hood);
      else if (cooking && hood) ctx.placed.push({ entry: hood, role: 'hood', position: cooking.position, rotation: cooking.rotation, elevation: 1.5 });
      const basin = ctx.placed.find(p => p.role === 'sink');
      const shelfWall = basin && wallBehind(ctx, basin);
      const shelf = pickForRole('kitchen_shelf', catalog);
      if (shelf && shelfWall && shelf.placement === 'wall') {
        const along = (basin.position.x - shelfWall.start.x) * shelfWall.dir.x + (basin.position.z - shelfWall.start.z) * shelfWall.dir.z;
        const n = inwardNormal(room, shelfWall), size = sizeOf(shelf), reach = shelfWall.thickness / 2 + size.d / 2 + .005;
        const clamped = Math.max(size.w / 2, Math.min(shelfWall.length - size.w / 2, along));
        ctx.placed.push({ entry: shelf, role: 'kitchen_shelf', wallId: shelfWall.id, position: round({ x: shelfWall.start.x + shelfWall.dir.x * clamped + n.x * reach, z: shelfWall.start.z + shelfWall.dir.z * clamped + n.z * reach }), rotation: rotationFacing(n), elevation: Math.min(shelfWall.height - size.h, 1.5) });
      }
      commitOnTop(ctx, cooking, 'kitchen_decor', -(sizeOf(cooking?.entry ?? run[0]).w / 2 - .25), -.12);
      commitOnTop(ctx, basin, 'cutting_board', .3, -.05);
      const runWall = ctx.used[0];
      const counterDepth = Math.max(0, ...run.map(entry => entry.size[2]));
      const table = commitDining(ctx, runWall, false, 4, counterDepth + .9);
      if (table) commitCeiling(ctx, 'pendant', table.position); else commitCeiling(ctx, 'pendant', centre);
      if (runWall && cooking) {
        const runner = pickForRole('runner', catalog);
        if (runner) {
          const at = inFromWall(room, runWall, counterDepth + .55);
          const rotation = rotationFacing(inwardNormal(room, runWall)) + Math.PI / 2;
          if (insideRoom(room, at, rotation, sizeOf(runner)) && !collisions(level, at, rotation, { ...sizeOf(runner), h: .5 }, ctx.placed).length) ctx.placed.push({ entry: runner, role: 'runner', position: at, rotation, elevation: 0 });
        }
      }
      commitCurtains(ctx);
      if (!small) commitCorner(ctx, 'plant');
      break;
    }
    case 'bathroom': {
      const bath = want(ctx, 'bath');
      if (bath) commitRun(ctx, [bath], ['bath'], { side, from: 'start', offset: .02 });
      const toilet = want(ctx, 'toilet');
      if (toilet) commitRun(ctx, [toilet], ['toilet'], { avoid: ctx.used, from: 'start', offset: .2 });
      const basin = pickForRole('basin', catalog);
      const [placedBasin] = basin ? commitRun(ctx, [basin], ['basin'], { from: 'center' }) : [];
      commitWallDecor(ctx, placedBasin, 'mirror', 1.6);
      commitBeside(ctx, placedBasin, 'towel_stand', ['left', 'right'], .1, true);
      const washer = pickForRole('washer', catalog);
      if (washer && !small) { const before = ctx.skipped.length; commitRun(ctx, [washer], ['washer'], { avoid: [], from: 'end', offset: .05 }); ctx.skipped.length = before; }
      if (Math.min(maxX - minX, maxZ - minZ) >= 2.4) { const shower = pickForRole('shower', catalog); if (shower) { const before = ctx.skipped.length; commitRun(ctx, [shower], ['shower'], { avoid: [], from: 'end', offset: .02 }); ctx.skipped.length = before; } }
      if (!small) commitCorner(ctx, 'plant');
      break;
    }
    case 'office': {
      const desk = commitDesk(ctx, side, true);
      commitWallDecor(ctx, desk, 'art', 1.6);
      const chair = ctx.placed.find(p => p.role === 'office_chair' || p.role === 'chair');
      const shelf = want(ctx, 'bookshelf');
      if (shelf) commitRun(ctx, [shelf], ['bookshelf'], { avoid: ctx.used, from: 'start', offset: .1 });
      if (roomy) { const storage = pickForRole('dresser', catalog); if (storage) { const before = ctx.skipped.length; commitRun(ctx, [storage], ['dresser'], { avoid: ctx.used, from: 'start', offset: .1 }); ctx.skipped.length = before; } }
      if (desk) commitRug(ctx, [desk, ...(chair ? [chair] : [])]);
      if (!small) commitBeside(ctx, desk, 'floor_lamp', ['left', 'right'], .15, true);
      commitCurtains(ctx);
      if (!small) commitCorner(ctx, 'plant');
      commitCeiling(ctx, 'pendant', centre);
      break;
    }
  }
  return { placed: ctx.placed, skipped: ctx.skipped.map(text => text.replace(/\.+$/, '')) };
}

export { frontVector, rightVector };
export type { Placement };
